// =============================================================================
// rateLimit
// =============================================================================
// PURPOSE: Client-side rate limiting to prevent abuse and rapid submissions.
//
// ARCHITECTURE:
// - Sliding window implementation using localStorage
// - Records timestamps of all requests in current window
// - Resets window when expired (windowMs has passed)
// - Returns: allowed (boolean), retryAfter (ms), remaining (count)
//
// PRE-CONFIGURED LIMITERS:
// - loginLimiter: 5 attempts per 15 minutes (brute force protection)
// - webhookLimiter: 50 calls per minute (N8N rate limiting)
//
// KEY DECISIONS:
// - Persists across page reloads (localStorage)
// - formatRetryTime for user-friendly error messages ("2 minutes", "30 seconds")
// - Silent failures on localStorage errors (doesn't break UX)
// =============================================================================

import { logger } from "../lib/logger";

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  storageKey: string;
}

interface RateLimitResult {
  allowed: boolean;
  retryAfter?: number; // milliseconds until next attempt allowed
  remaining?: number; // requests remaining in current window
}

interface RateLimitRecord {
  timestamps: number[];
  windowStart: number;
}

/**
 * Creates a rate limiter function (internal use)
 */
function createRateLimiter(config: RateLimitConfig) {
  const { maxRequests, windowMs, storageKey } = config;

  return function checkRateLimit(): RateLimitResult {
    const now = Date.now();

    // Get existing records from localStorage
    let record: RateLimitRecord;
    try {
      const stored = localStorage.getItem(storageKey);
      record = stored ? JSON.parse(stored) : { timestamps: [], windowStart: now };
    } catch {
      record = { timestamps: [], windowStart: now };
    }

    // Reset window if expired
    if (now - record.windowStart >= windowMs) {
      record = { timestamps: [], windowStart: now };
    }

    // Remove timestamps outside the current window
    const windowStart = now - windowMs;
    record.timestamps = record.timestamps.filter((ts) => ts > windowStart);

    // Check if limit exceeded
    if (record.timestamps.length >= maxRequests) {
      const oldestTimestamp = Math.min(...record.timestamps);
      const retryAfter = oldestTimestamp + windowMs - now;

      return {
        allowed: false,
        retryAfter: Math.max(0, retryAfter),
        remaining: 0,
      };
    }

    // Add current timestamp and save
    record.timestamps.push(now);
    try {
      localStorage.setItem(storageKey, JSON.stringify(record));
    } catch (e) {
      logger.error("Failed to save rate limit state:", e);
    }

    return {
      allowed: true,
      remaining: maxRequests - record.timestamps.length,
    };
  };
}

/**
 * Format retry time for user display
 *
 * @param ms - Milliseconds until retry allowed
 * @returns Human-readable string
 */
export function formatRetryTime(ms: number): string {
  const seconds = Math.ceil(ms / 1000);

  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? "" : "s"}`;
  }

  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

// Pre-configured rate limiters

/**
 * Login rate limiter: 5 attempts per 15 minutes
 * Prevents brute force attacks
 */
export const loginLimiter = createRateLimiter({
  maxRequests: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  storageKey: "rateLimit:login",
});

/**
 * Webhook trigger limiter: 50 calls per minute
 * N8N-specific rate limiting
 */
export const webhookLimiter = createRateLimiter({
  maxRequests: 50,
  windowMs: 60 * 1000, // 1 minute
  storageKey: "rateLimit:webhook",
});
